import React, { useState } from 'react';
import { action, computed } from 'mobx';
import { observer } from 'mobx-react';

import { useClientStore } from 'apps/hooks';
import { useViewerControl } from 'libs/rendering/gltf/hooks';

import Loading from 'components/Loading';
import { HeaderWithClose } from 'components';
import { CADPanelType, CADPanelID } from 'constants/cad';
import { CADSidebarWidth, CADPanelWidth } from 'styles/constants';

import { themes } from 'styles/themes/wikifactoryNew';

import { AnnotationsPanel } from './AnnotationsPanel';
import { MeasurementsPanel } from './MeasurementsPanel';
import { UiModes } from '../../config';

import Panel from '../panel/panel';
import { AssemblyTree } from '../tree';
import { Geometry } from '../geometry';
import { ManufacturingFeatures } from '../manufacturingFeatures';

class LeftPanelState {
  constructor(viewerControl) {
    this.viewerControl = viewerControl;
  }

  // left Panel "resizable" width
  @computed get leftPanelResizeWidth() {
    if (this.viewerControl.uiMode !== UiModes.collapsed) {
      return this.left;
    }
    return 0;
  }

  @action SetLeftPanelResizeWidth = size => {
    this.left = size;
  };

  @action Collapse = () => {
    this.viewerControl.UpdateUiMode(UiModes.collapsed);
  };
}

const LeftPanels = observer(function _LeftPanels() {
  const viewerControl = useViewerControl();
  const clientStore = useClientStore();

  const [panelState] = useState(() => new LeftPanelState(viewerControl));
  const panelRenderMap = {
    [CADPanelType.AnnotationPanel]: <AnnotationsPanel />,
    [CADPanelType.MeasurementPanel]: <MeasurementsPanel />,
  };

  const camera = viewerControl?.viewer?.settings?.CurrentCamera();
  const hasActivePanel = !!panelRenderMap[viewerControl.activePanelType];

  if (!camera && !hasActivePanel) return null;
  if (camera.IsDrawing() && !hasActivePanel) return null;

  const { debugFeatures } = viewerControl;
  const hasIntermediatorRole = clientStore.hasRole('INTERMEDIATOR');
  const isIntermediatorDebug = hasIntermediatorRole && debugFeatures;
  const shouldHideGeometry = true;

  let inner = panelRenderMap[viewerControl.activePanelType];

  const tabLoadingStatus = camera.GetLoadStatus();
  if (viewerControl.uiMode === UiModes.assembly) {
    const root = camera.HasSubAssembly()
      ? camera.GetSubAssembly()
      : viewerControl?.cadFile?.assembly?.GetProduct(0);

    inner = <AssemblyTree root={root} key={root?.uuid} />;
  } else if (viewerControl.uiMode === UiModes.section) {
    // DISABLED BY NOW... this may go in toolbar
    // browserIcon = 'clip_section';
    // inner = <SectionBrowser />;
  } else if (
    !isIntermediatorDebug &&
    viewerControl.uiMode === UiModes.geometry &&
    !shouldHideGeometry
  ) {
    inner =
      tabLoadingStatus !== 0 ? (
        <Loading text='Crunching BREP data' isLayoutCentered />
      ) : (
        <Geometry />
      );
  } else if (
    // DEBUG TOOL
    viewerControl.uiMode === UiModes.feature ||
    viewerControl.uiMode === UiModes.wallFeature ||
    camera.useSurfaceColors
  ) {
    inner =
      tabLoadingStatus !== 0 ? (
        <Loading text='Crunching Feature data' isLayoutCentered />
      ) : (
        <ManufacturingFeatures />
      );
  }

  const getTitle = uiMode => {
    if (uiMode === UiModes.assembly) {
      return 'Assembly Explorer';
    }

    if (!isIntermediatorDebug && uiMode === UiModes.geometry) {
      return 'BREP';
    }
  };

  const panelStyle = {
    minWidth: CADPanelWidth,
    width: 'fit-content',
    background: hasActivePanel ? themes.color.wfWhite : 'transparent',
  };

  return (
    <>
      {inner && (
        <div id={CADPanelID} style={panelStyle} className='h-full'>
          <Panel
            data-testid={CADPanelID}
            position='left'
            leftPanelState={panelState}
            isOpen={true}
            overlay
            overlayStyle={{
              bottom: 0,
              pointerEvents: 'all',
              left: CADSidebarWidth,
              top: clientStore.PageHeaderHeight,
            }}
            style={panelStyle}
          >
            {!clientStore.isMobile && getTitle(viewerControl.uiMode) && (
              <div className=' border-b border-wfGrey100'>
                <HeaderWithClose
                  className='py-1'
                  title={getTitle(viewerControl.uiMode)}
                  onClose={() => {
                    viewerControl.UpdateUiMode(UiModes.collapsed);
                  }}
                />
              </div>
            )}
            {inner}
          </Panel>
        </div>
      )}
    </>
  );
});

export default LeftPanels;
